import { privateAgent } from "@lib/Requests/AuthRequests";
import { PaigeResArr, PaigeResObj } from "@lib/Requests/types";
import { routesName } from "@lib/RoutesName";
import { AddBehaviorProps, BehaviorProps, EditBehaviorProps } from "@lib/Types";
import { useSnackbar } from "notistack";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useState, useEffect } from "react";
import { handleAxiosError } from "@lib/Requests/errorHandler";

const behaviorRequest = async () => {
  try {
    const { data } = await privateAgent.get<PaigeResArr<BehaviorProps>>(
      routesName.BehaviorRoute({}).get_behavior
    );

    if (data.status === 200) {
      const results = data.result.items;
      return results;
    } else {
      throw new Error(data.error || "Failed to fetch behaviors");
    }
  } catch (error: any) {
    handleAxiosError(error, 'Failed to fetch behaviors')
  }
};

const updateBehaviorRequest = async ({ id, data }: { id: string; data: EditBehaviorProps }) => {
  try {
    const { id: _, ...restData } = data; 
    const res = await privateAgent.put<PaigeResObj<BehaviorProps>>(
      routesName.BehaviorRoute({ id }).update_behavior,
      restData
    );
    if (res.data && res.data.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data.error || "Failed to update behavior");
    }
  } catch (error) {
    handleAxiosError(error, 'Failed to update behaviors')
  }
};

const addBehaviorRequest = async ({ id, data }: { id: string; data: AddBehaviorProps }) => {
  try {
    const res = await privateAgent.post<PaigeResObj<BehaviorProps>>(
      routesName.BehaviorRoute({ id }).add_behavior,
      data
    );
    if (res.data && res.data.status === 201) {
      return res.data;
    } else {
      throw new Error(res.data.error || "Failed to update behavior");
    }
  } catch (error) {
    handleAxiosError(error, 'Failed to add behaviors')

  }
};

const deleteBehaviorRequest = async (id: string) => {
  try {
    const res = await privateAgent.delete<PaigeResObj<BehaviorProps>>(
      routesName.BehaviorRoute({ id }).delete_behavior
    );
    if (res.data && res.data.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data.error || "Failed to delete behavior");
    }
  } catch (error) {
    handleAxiosError(error, 'Failed to delete behaviors')

  }
};

export const useBehaviors = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [filteredData, setFilteredData] = useState<BehaviorProps[]>([]);
  const [searchQueries, setSearchQueries] = useState<{ searchQuery: string; behaviorCategoryId: string }>({
    searchQuery: "",
    behaviorCategoryId: "0",
  });

  const { isLoading, data, error } = useQuery({
    queryKey: ["BehaviorQuery"],
    queryFn: behaviorRequest,
    suspense: true,
    onSuccess: (data) => {
      if (data) {
        setFilteredData(data);
      }
    },
    onError: (error: any) => {
      console.error("Error fetching behaviors:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  const updateBehaviorMutation = useMutation(updateBehaviorRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries("BehaviorQuery");
      enqueueSnackbar("Behavior updated successfully", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error updating behavior:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  const addBehaviorMutation = useMutation(addBehaviorRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries("BehaviorQuery");
      enqueueSnackbar("Behavior added successfully", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error adding behavior:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  const deleteBehaviorMutation = useMutation(deleteBehaviorRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries("BehaviorQuery");
      enqueueSnackbar("Behavior deleted successfully", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error deleting behavior:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  useEffect(() => {
    if (data) {
      const filtered = data.filter((item: BehaviorProps) => {
        const hasBehaviorCategory = item.behaviorCategoryId == searchQueries.behaviorCategoryId;
        const hasBehavior = item.name.toLowerCase().includes(searchQueries.searchQuery.toLowerCase());

        let toFiltered = true;
        if (searchQueries.searchQuery !== "") {
          toFiltered = toFiltered && hasBehavior;
        }

        if (searchQueries.behaviorCategoryId !== "0") {
          toFiltered = toFiltered && hasBehaviorCategory;
        }
        return toFiltered;
      });
      setFilteredData(filtered);
    }
  }, [data, searchQueries]);

  const handleSearchQuery = (searchQuery: { searchQuery: string; behaviorCategoryId: string }) => {
    setSearchQueries(searchQuery);
  };

  return { isLoading, data, filteredData, setFilteredData, error, handleSearchQuery, updateBehaviorMutation, addBehaviorMutation, deleteBehaviorMutation };
};

